/**
 * Wave V — 2D system-variable commands. `NAME VALUE` syntax (e.g.
 * FILLMODE 0) writes the persisted store; bare NAME reports the value.
 * Behavior integrations read the same store (mirrors/engine layers).
 * (docs/architecture/command-parity-plan.md)
 */

import { useSystemVarsStore } from '../../../state/useSystemVarsStore';
import type { SystemVars } from '../../../state/useSystemVarsStore';
import { notify } from '../../../utils/notify';
import i18n from '../../../i18n';
import type { RunnerMap } from '../runnerRegistry';

/** Map canonical action ids to the variable name they drive. */
const VAR_BY_ID: Record<string, keyof SystemVars> = {
    'var.filedia': 'FILEDIA',
    'var.mirrtext': 'MIRRTEXT',
    'var.pickfirst': 'PICKFIRST',
    'var.whipthread': 'WHIPTHREAD',
    'var.dragmode': 'DRAGMODE',
    'var.highlight': 'HIGHLIGHT',
    'var.isave': 'ISAVEPERCENT',
    'var.savetime': 'SAVETIME',
    'var.cmddia': 'CMDDIA',
    'var.edgemode': 'EDGEMODE',
    'var.texted': 'TEXTED',
    'var.cannoscale': 'CANNOSCALE',
    'var.fillmode': 'FILLMODE',
    'var.hpbound': 'HPBOUND',
    'var.drawOrderCtl': 'DRAWORDERCTL',
    'var.mbuttonpan': 'MBUTTONPAN',
    'var.cursorSize': 'CURSORSIZE',
    'var.pickbox': 'PICKBOX',
    'var.zoomfactor': 'ZOOMFACTOR',
    'var.rollovertips': 'ROLLOVERTIPS',
    'var.clipromptlines': 'CLIPROMPTLINES',
    'var.cmdlinefadetime': 'COMMANDLINEFADETIME',
    'var.cmdlineopacity': 'COMMANDLINEOPACITY',
    'var.ucsDetect': 'UCSDETECT',
    'var.pdmode': 'PDMODE',
    'var.pdsize': 'PDSIZE',
    'var.fillradius': 'FILLETRADIUS',
    'var.polygonsides': 'POLYGONSIDES',
    'var.polygontype': 'POLYGONTYPE',
    'var.fieldeval': 'FIELDEVAL',
    'var.fielddisplay': 'FIELDDISPLAY',
};

/** Value clamps (Faz 11): OCS command_line.rs / drafting_settings ranges. */
const VAR_CLAMPS: Partial<Record<keyof SystemVars, [number, number]>> = {
    PICKBOX: [0, 50],
    ZOOMFACTOR: [3, 100],
    CLIPROMPTLINES: [0, 50],
    COMMANDLINEFADETIME: [0, 60000],
    COMMANDLINEOPACITY: [20, 100],
    CURSORSIZE: [1, 100],
};

/** POLAR/GRID/SNAP/ORTHO toggles wired through the view store + snap store. */
export function sysvarSet(id: string, args?: string[]): string {
    const varName = VAR_BY_ID[id];
    if (!varName) return i18n.t('tools.notify.unknownSysvar');
    const store = useSystemVarsStore.getState();
    const current = store[varName];
    if (!args || args.length === 0 || args[0].toUpperCase() === '?') {
        const msg = i18n.t('tools.cmd.sysvarCurrent', { name: varName, value: current });
        notify(msg, 'info');
        return msg;
    }
    // 2D accepts ON/OFF as 1/0 for most toggles.
    const raw = args[0].toUpperCase();
    const num = raw === 'ON' ? 1 : raw === 'OFF' ? 0 : Number(raw);
    if (!Number.isFinite(num)) return i18n.t('tools.cmd.sysvarInvalid', { name: varName, value: args[0] });
    const [min, max] = VAR_CLAMPS[varName] ?? [-Number.MAX_VALUE, Number.MAX_VALUE];
    const clamped = Math.min(max, Math.max(min, num));
    store.set(varName, clamped);
    const msg = i18n.t('tools.cmd.sysvarSet', { name: varName, value: clamped });
    notify(msg, 'success');
    return msg;
}

const runners: RunnerMap = {};
for (const id of Object.keys(VAR_BY_ID)) {
    runners[id] = (args) => sysvarSet(id, args);
}

export const sysvarRunners: RunnerMap = runners;
